"use client";

import { IconChevronRight } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { Img } from "@/components/image";
import { DietCardBadges } from "@/features/cart/components/configure-diet/diet-card/diet-card-badges";
import { DietCardCateringLogo } from "@/features/cart/components/configure-diet/diet-card/diet-card-catering-logo";
import { DietCardDeliveryRow } from "@/features/cart/components/configure-diet/diet-card/diet-card-delivery-row";
import { DietCardMenuRow } from "@/features/cart/components/configure-diet/diet-card/diet-card-menu-row";
import { DietCardReviewsRow } from "@/features/cart/components/configure-diet/diet-card/diet-card-reviews-row";
import { DietCateringInfoDialog } from "@/features/diets/components/diet-info-dialog/diet-catering-info-dialog";
import { stripHtml } from "@/features/diets/helpers/strip-html";
import type { DietCatalogDetail } from "@/features/diets/types";
import type { Globals } from "@/features/globals/types/globals.type";
import { Link } from "@/i18n/navigation";
import {
  formatCateringName,
  formatDietName,
} from "@/lib/helpers/format-brand-name";

const CATERING_LOGO_SIZE = 40;
const DIET_IMAGE_SIZE = 48;

export const DietCardMobile = ({
  diet,
  globals,
  kcal,
  variant,
}: {
  diet: DietCatalogDetail;
  globals?: Globals | null;
  kcal?: string;
  variant?: string;
}) => {
  const t = useTranslations("diets");

  return (
    <div className="overflow-hidden rounded-lg border border-brand-grey2-300 bg-white">
      <div className="flex items-start gap-3 bg-brand-grey1-100 p-4">
        {diet.imageUrl && (
          <div className="h-12 w-12 overflow-clip rounded-lg">
            <Img
              alt={formatDietName(diet.name)}
              className="block shrink-0 rounded-lg object-cover"
              height={DIET_IMAGE_SIZE}
              src={diet.imageUrl}
              width={DIET_IMAGE_SIZE}
            />
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="font-medium text-lg leading-6.5" data-pw="diet-header">
            {formatDietName(diet.name)}
          </p>
          <DietCardBadges diet={diet} />
        </div>
      </div>
      {/* The dialog trigger covers the whole row, so the catering link can sit
      on top of it instead of being nested inside a button. */}
      <div className="relative flex w-full items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-brand-grey1-100">
        <DietCateringInfoDialog
          diet={diet}
          trigger={
            <button className="absolute inset-0 cursor-pointer" type="button">
              <span className="sr-only">{t("info.title")}</span>
            </button>
          }
        />
        <DietCardCateringLogo
          imageUrl={diet.catering.logo.url}
          name={formatCateringName(diet.catering.name)}
          size={CATERING_LOGO_SIZE}
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="truncate font-medium text-base">
            <Link
              className="relative z-10 hover:underline"
              href={{
                pathname: "/caterings/[slug]",
                params: { slug: diet.catering.slug },
              }}
            >
              {formatCateringName(diet.catering.name)}
            </Link>
          </div>
          {diet.description && (
            <div className="truncate text-brand-green2-300 text-sm">
              {stripHtml(diet.description)}
            </div>
          )}
        </div>
        <IconChevronRight
          className="size-6 shrink-0 text-brand-green1-300"
          stroke={1.5}
        />
      </div>
      <DietCardReviewsRow compact diet={diet} globals={globals} />
      <DietCardDeliveryRow diet={diet} />
      <DietCardMenuRow compact dietId={diet.id} kcal={kcal} variant={variant} />
    </div>
  );
};
